import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import snakeCase from "lodash/snakeCase";
import Box from "@mui/material/Box";
import { isFieldSettingEnabled } from "../../utils/fieldSettings";
import { RECEIVING_WIZARD_ONLY_FIELDS } from "../../constants/fields";
import { isOnsiteCaptureProject } from "../modals/ProjectActions/projectReceivingWorkflow";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import Paper from "@mui/material/Paper";

import useAuth from "../../hooks/useAuth";
import { useConfigurableFields } from "../../contexts/configurable-fields";
import { useFieldSettings } from "../../contexts/customFields";
import useAssetsTable from "../../hooks/useAssetsTable";
import { useAssetsCollection } from "../../contexts/assetsCollection";
import { useFullScreen } from "../../contexts/fullScreen";
import BulkActions from "../../components/AssetsTable/BulkActions";
import useNotification from "../../hooks/notification";
import useAssetsImport from "../../hooks/useAssetsImport";
import { useUndo } from "../../contexts/undo";
import Table from "../TablePro/Table/index";
import TableSettings from "../TablePro/TableSettings";
import TableSettingsLegacy from "../TablePro/TableSettingsLegacy";
import AssetsSearch from "./AssetsSearch";
import useTableBulkScrubber from "../TablePro/extensions/useTableBulkScrubber";
import useAssetImageModal from "../TablePro/extensions/useAssetImageModal";
import AssetsTableActions from "./AssetsTableActions";
import AssetsTableActionsLegacy from "./AssetsTableActionsLegacy";
import ReceivingWizardModal from "../../components/capture/ReceivingWizard/ReceivingWizardModal";

import MoveLocationsModal from "../modals/MoveLocationsModal/MoveLocationsModal";
import SelectProjectAttentionModal from "../capture/SelectProjectAttentionModal";
import Undo from "./Undo";
import useQueryParams from "../../hooks/useQueryParams";
import useScrubberCollectionWithCustomFields from "../../hooks/scrubber/useScrubberCollectionWithCustomFields";
import { usePermissions } from "../../contexts/permissions";
import { useProject } from "../../contexts/project";
import Filter from "../../layouts/ProjectLayout/FilterNav/Filter";
import { useProjectInventory } from "../../contexts/projectInventory";
import ActiveFilterChips from "../ActiveFilterChips";
import useSelectionContext from "../../hooks/useSelectionContext";
import { buildStoreSettings } from "../TablePro/extensions/useTablePro";

export default function AssetsTable({
  clientId,
  projectId,
  size,
  order,
  fields: tableFields,
  updateSettings,
  moreActions = true,
  fieldsProps: defaultFieldsProps,
}) {
  const navigate = useNavigate();
  const params = useQueryParams();
  const { undo } = useUndo();
  const { user } = useAuth();
  const { can } = usePermissions();
  const [isAttentionOpen, setIsAttentionOpen] = useState(false);
  const { fullScreen, setFullScreen } = useFullScreen();
  const toggleFullScreen = () => setFullScreen(prev => !prev);
  const { notifyError } = useNotification();
  const isMobileMode = true;
  const { settings } = user.company;
  const {
    loading,
    loadingMutation,
    setLoadingMutation,
    collection: collectionWithCustomFields,
    setCollection,
    handeRefetch,
    onLoadMore,
    totalIds,
    fetchTotalIds,
    totalCount,
    metadata,
    setMetadata,
    createAsset,
    updateImage,
    removeImage,
    bulkCreateImages,
    search,
    setSearch,
    bulkRemoveAssets,
    limit,
  } = useAssetsCollection();
  const loadingTable = loading || loadingMutation;
  const { handleUpdate, handleBulkUpdate } = useAssetsTable();
  const currentProject = useProject();
  const onsiteInventoryCapture = currentProject?.settings?.onsite_inventory_capture;
  const { customFields: allCustomFields = [] } = useConfigurableFields() || {};
  const customFields = useFieldSettings(currentProject);
  const [disabledScroll, setDisabledScroll] = useState(false);
  const openMoveModal = params.get("moveModal") === "true";
  const collection = useScrubberCollectionWithCustomFields(collectionWithCustomFields);
  const visibleCollectionIds = useMemo(() => collection.map(entity => entity.id), [collection]);
  const { setClientId, setProjectId, inputProjectId } = useProjectInventory();
  const { updateSelectionContext } = useSelectionContext();
  // Permission checks
  const canWrite = can("assets.write");

  const fields = useMemo(() => {
    let processedFields;

    if (isMobileMode) {
      processedFields = tableFields.map(field => ({
        ...field,
        visible: field.alwaysVisible ? true : field.visible,
      }));
    } else {
      processedFields = tableFields.map(field => ({
        ...field,
        visible: field.alwaysVisible ? true : field.visible,
        inactive: field.system ? false : !isFieldSettingEnabled(customFields[snakeCase(field.attrName || field.id)]),
      }));
    }

    // Onsite Inventory Capture:
    // Apply per-field rules (defined in tablePro config) so fields can be forced
    // visible/view-only without mutating user settings.
    if (onsiteInventoryCapture) {
      processedFields = processedFields.map(field => {
        const rule = field.onsiteInventoryCapture;
        if (!rule) return field;

        const triggerField = processedFields.find(f => f.id === rule.showWhenFieldDisplayed);
        const triggerDisplayed = Boolean(triggerField?.visible && !triggerField?.inactive);

        if (!rule.viewOnly) {
          return { ...field, visible: triggerDisplayed };
        }

        return {
          ...field,
          visible: triggerDisplayed,
          inactive: false,
          unEditable: true,
          disableFillDown: true,
        };
      });
    }

    if (isOnsiteCaptureProject(currentProject)) {
      processedFields = processedFields.map(field => {
        if (!RECEIVING_WIZARD_ONLY_FIELDS.includes(snakeCase(field.id))) return field;
        return { ...field, visible: false, inactive: true };
      });
    }

    return processedFields;
  }, [tableFields, customFields, onsiteInventoryCapture, isMobileMode, currentProject]);

  // Create current table settings for export synchronization
  const currentTableSettings = useMemo(
    () =>
      buildStoreSettings(
        {
          size,
          limit: 45, // Default limit from useTablePro
          order,
          version: "v10", // Default version from table settings
          fields,
        },
        user.assetsTableSettings,
        allCustomFields
      ),
    [size, order, fields, user.assetsTableSettings, allCustomFields]
  );

  const { selectedIds, unSelectBulk, props: bulkProps } = useTableBulkScrubber(totalIds, {
    orderedIds: visibleCollectionIds,
    fetchTotalIds,
  });

  const { ImageModal, props: photoProps } = useAssetImageModal({
    collection,
    updateImage,
    removeImage,
    bulkCreateImages,
    id: "assetId",
  });

  const fieldsProps = useMemo(
    () => ({
      BULK_CHECKBOX: bulkProps,
      // photoCarousel: photoProps,
      ...defaultFieldsProps,
    }),
    [totalIds, selectedIds, defaultFieldsProps]
  );

  const getRowBackgroundColor = useCallback(
    entity => {
      if (entity && selectedIds.includes(entity.id)) {
        return "#E8F0FE";
      }
      return entity && entity.allocatedQuantity > 0 ? "#e3f2fd" : undefined;
    },
    [selectedIds]
  );

  const handleToggle = () => {
    const pathname = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    if (params.get("receiving")) {
      const returnTo = params.get("returnTo");
      if (returnTo && returnTo.startsWith("/")) {
        navigate(returnTo, { replace: true });
        return;
      }

      handeRefetch();
      params.delete("receiving");
      params.delete("step");
      params.delete("editShipment");
      params.delete("shipmentId");
      params.delete("id");
      navigate({ search: params.toString() });
    } else {
      params.set("step", "details");
      params.set("receiving", "true");
      navigate({ pathname, search: params.toString() });
    }
  };

  const isReceivingWizardDisabled = params.get("disable_receiving_wizard") === "true";

  const handleCreateAsset = () => {
    if (!projectId) {
      setIsAttentionOpen(true);
      return;
    }

    if (currentProject?.storageEnabled && !isReceivingWizardDisabled) {
      handleToggle();
    } else {
      setDisabledScroll(true);
      createAsset({
        variables: { projectId: projectId, input: { projectId } },
        onSuccess: () => {
          setDisabledScroll(false);
        },
        onFailure: errors => {
          notifyError(errors);
        },
      });
    }
  };

  const toggleMoveModal = () => {
    if (openMoveModal) {
      params.delete("moveModal");
    } else {
      params.set("moveModal", "true");
    }
    navigate({ search: params.toString() });
  };

  const onSelectProject = ({ clientId, projectId }, search) => {
    updateSelectionContext({
      client_id: clientId || "",
      project_id: projectId || "",
    });
    setClientId(clientId);
    setProjectId(projectId);
    if (projectId && projectId !== "all") {
      navigate({ pathname: `/projects/${projectId}/assets`, search });
    } else if (clientId) {
      navigate({ pathname: `/clients/${clientId}/assets`, search });
    }
  };

  // Keeps the open scan's params so it reruns against all customers.
  const onSearchAllCustomers = () =>
    onSelectProject({ clientId: "null", projectId: "all" }, params.toString());

  const onSelectClient = clientId => {
    updateSelectionContext({
      client_id: clientId || "",
      project_id: "all",
    });
    setClientId(clientId);
    setProjectId("all");
    navigate(`/clients/${clientId}/assets`);
  };

  useEffect(() => {
    const handleKeyDown = event => {
      const isCmdOrCtrl = event.metaKey || event.ctrlKey;
      if (isCmdOrCtrl && event.keyCode === 90) {
        const item = undo();
        if (item) {
          handleBulkUpdate(item[0], { saveUndo: false });
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [undo]);

  useAssetsImport(user?.id, () => {
    handeRefetch();
  });

  return (
    <div>
      <Paper sx={{ p: 2 }}>
        <Filter
          clientId={clientId}
          projectId={inputProjectId}
          onSelectClient={onSelectClient}
          onSelectProject={onSelectProject}
        />
        <Box mb={2.5} />
        {metadata.totalCount >= 0 && (
          <Box display="flex" alignItems="center" mt={-0.5} pb={{ sm: 2 }} lineHeight={1}>
            <Box mr={1}>{metadata.totalCount} Items</Box>
            <ActiveFilterChips />
          </Box>
        )}
        <Box display="flex" flexDirection={{ xs: "column", lg: "row" }}>
          <Box flexGrow={1}>
            <Box display="flex" alignItems="center">
              <AssetsSearch
                search={search}
                setSearch={setSearch}
                loading={loadingTable}
                receivingWizard={settings.receiving_wizard}
                fields={fields}
              />
              <Box mr={1} />
              {canWrite && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleCreateAsset}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Add New
                </Button>
              )}
            </Box>
          </Box>
          <SelectProjectAttentionModal
            open={isAttentionOpen}
            onClose={() => setIsAttentionOpen(false)}
          />
          <Box flexGrow={1} mt={{ xs: 1, sm: 2, lg: 0 }}>
            {settings.receiving_wizard && canWrite && (
              <Button variant="outlined" color="primary" onClick={toggleMoveModal}>
                <Box mr={1}>
                  <LocalShippingIcon />
                </Box>
                Inventory Scan
              </Button>
            )}
            {openMoveModal && (
              <MoveLocationsModal
                open={openMoveModal}
                onClose={toggleMoveModal}
                clientId={clientId}
                projectId={projectId}
                onSearchAllCustomers={onSearchAllCustomers}
              />
            )}
          </Box>
          <Box flexGrow={1} mt={{ xs: 1, sm: 2, lg: 0 }}>
            <Box display="flex" alignItems="center" justifyContent="flex-end">
              {isMobileMode ? (
                <>
                  <Box mr={1}>
                    <BulkActions
                      selectedIds={selectedIds}
                      totalCount={totalCount}
                      onCompleteBulk={unSelectBulk}
                      refetch={handeRefetch}
                      setDisabledScroll={setDisabledScroll}
                      bulkRemoveAssets={bulkRemoveAssets}
                      currentTableSettings={currentTableSettings}
                      clientId={clientId}
                      projectId={projectId}
                      currentProject={currentProject}
                    />
                  </Box>
                  <TableSettings
                    type="scrubber"
                    fullScreen={fullScreen}
                    toggleFullScreen={toggleFullScreen}
                    refetch={handeRefetch}
                    limit={limit}
                    size={size}
                    selectedIds={selectedIds}
                    fields={fields}
                    onChange={updateSettings}
                  />
                  <Box mr={1} />
                  <AssetsTableActions
                    moreActions={moreActions}
                    setLoading={setLoadingMutation}
                    setCollection={setCollection}
                    setMetadata={setMetadata}
                    setDisabledScroll={setDisabledScroll}
                    refetch={handeRefetch}
                    fullScreen={fullScreen}
                    toggleFullScreen={toggleFullScreen}
                    size={size}
                    onChange={updateSettings}
                    createAsset={createAsset}
                    enableSize
                    actions={<Undo />}
                    fields={fields}
                  />
                </>
              ) : (
                <>
                  <AssetsTableActionsLegacy
                    moreActions={moreActions}
                    setLoading={setLoadingMutation}
                    setCollection={setCollection}
                    setMetadata={setMetadata}
                    selectedIds={selectedIds}
                    onCompleteBulk={unSelectBulk}
                    totalCount={totalCount}
                    setDisabledScroll={setDisabledScroll}
                    refetch={handeRefetch}
                    fields={fields}
                  />
                  <Box mr={1} />
                  <TableSettingsLegacy
                    fullScreen={fullScreen}
                    toggleFullScreen={toggleFullScreen}
                    refetch={handeRefetch}
                    limit={limit}
                    size={size}
                    selectedIds={selectedIds}
                    fields={fields}
                    onChange={updateSettings}
                  />
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
      <Box mb={{ xs: 1, sm: 2 }} />
      <Card>
        <Table
          fullScreen={fullScreen}
          size={size}
          disabledScroll={disabledScroll}
          totalCount={totalCount}
          order={order}
          loading={loadingTable}
          loadingFields={false}
          fields={fields}
          fieldsProps={fieldsProps}
          entities={collection}
          onLoadMore={onLoadMore}
          onChangeEntity={handleUpdate}
          onChangeFields={updateSettings}
          onFillDown={handleBulkUpdate}
          onCopyPaste={handleBulkUpdate}
          getRowBackgroundColor={getRowBackgroundColor}
        />
      </Card>
      {ImageModal}
      {params.get("receiving") && <ReceivingWizardModal onToggle={handleToggle} />}
    </div>
  );
}
